/**
 * SortableTableHeader Component
 * Interactive table header with sorting functionality
 */

import React from 'react';
import { TableCell, Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { ColumnConfig, SortState, SortableColumn } from '../types/customerTable.types';
import { STYLES } from '../constants/customerTableConfig';
import { getSortIcon, getSortAriaLabel } from '../utils/sortUtils';

interface SortableTableHeaderProps {
  column: ColumnConfig;
  sortState: SortState;
  onSort: (columnId: SortableColumn) => void;
}

export const SortableTableHeader: React.FC<SortableTableHeaderProps> = ({
  column,
  sortState,
  onSort,
}) => {
  const isActive = sortState.column === column.id;
  const direction = isActive ? sortState.direction : null;

  // Non-sortable column
  if (!column.sortable) {
    return (
      <TableCell
        sx={{
          ...STYLES.tableCellHeader,
          ...(column.width && { width: column.width }),
          ...(column.id === 'actions' && { textAlign: 'center' }),
        } as SxProps<Theme>}
      >
        {column.label}
      </TableCell>
    );
  }

  // Sortable column
  const handleClick = () => {
    onSort(column.id as SortableColumn);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSort(column.id as SortableColumn);
    }
  };

  return (
    <TableCell
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      role="button"
      tabIndex={0}
      aria-label={getSortAriaLabel(column.label || '', isActive, direction)}
      sx={{
        ...STYLES.tableCellHeaderSortable,
        ...(column.width && { width: column.width }),
        ...(isActive && STYLES.tableCellHeaderActive),
      } as SxProps<Theme>}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {column.label}
        <Typography
          component="span"
          sx={{
            ...STYLES.sortIcon,
            ...(isActive && STYLES.sortIconActive),
          } as SxProps<Theme>}
        >
          {getSortIcon(isActive, direction)}
        </Typography>
      </Box>
    </TableCell>
  );
};

export default SortableTableHeader;
